import type { FC, PropsWithChildren } from "react";

import type { Accept, DropzoneOptions } from "react-dropzone";

import Dropzone from "react-dropzone";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import theme from "../theme";

export type FileDropzoneProps = {
    onDrop: DropzoneOptions["onDrop"];
    loading?: boolean;
    disabled?: boolean;
    accept?: Accept;
    maxFiles?: number;
    maxSize?: number;
    validity?: TPDFValidity;
    backgroundColor?: string;
    minHeight?: number;
    maxWidth?: string;
    ariaLabel?: string;
    inputId?: string;
};

const FileDropzone: FC<PropsWithChildren<FileDropzoneProps>> = ({
    onDrop,
    loading = false,
    disabled = false,
    accept = { "application/pdf": [".pdf"] },
    maxFiles = 1,
    maxSize,
    validity = "not_checked",
    backgroundColor,
    minHeight,
    maxWidth = "100%",
    children,
    ariaLabel,
    inputId = "file-upload-input",
}) => {
    const getBackgroundColor = () => {
        if (backgroundColor) return backgroundColor;

        switch (validity) {
            case "valid":
                return "#E0FFE3";
            case "invalid":
            case "invalid_id":
                return "#FFEEEE";
            case "not_checked":
            default:
                return "#F5F5F5";
        }
    };

    const getMinHeight = () => {
        if (minHeight) return minHeight;
        return validity === "valid" ? 130 : 180;
    };

    return (
        <Box
            component="section"
            role="region"
            aria-label={ariaLabel}
            style={{
                borderRadius: 8,
                backgroundColor: getBackgroundColor(),
                padding: "20px 40px 20px 40px",
                textAlign: "center",
                width: "100%",
                maxWidth,
                height: "100%",
                minHeight: getMinHeight(),
            }}
        >
            {loading ? (
                <Box
                    role="status"
                    aria-live="polite"
                    height={`calc(${getMinHeight()}px - 40px)`}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Dropzone onDrop={onDrop} maxFiles={maxFiles} maxSize={maxSize} accept={accept} disabled={disabled}>
                    {({ getRootProps, getInputProps, isDragActive }) => (
                        <div
                            {...getRootProps()}
                            role="button"
                            tabIndex={0}
                            aria-label={ariaLabel}
                            aria-pressed={isDragActive}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    document.getElementById(inputId)?.click();
                                }
                            }}
                            style={{
                                cursor: disabled ? "not-allowed" : "pointer",
                                outline: isDragActive ? `2px solid ${theme.palette.vbBlue.light}` : "none",
                            }}
                        >
                            {children}
                            <input {...getInputProps()} id={inputId} />
                        </div>
                    )}
                </Dropzone>
            )}
        </Box>
    );
};

export default FileDropzone;
